import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router';

import {
  FILTER_PARAM_PREFIX,
  FILTER_PROMPT_DEPLOYMENT_ID_KEY,
  FILTER_PROMPT_DEPLOYMENT_KEY,
  FILTER_PROMPT_DEPLOY_COUNT_KEY,
  FILTER_PROMPT_DEPLOY_ROLLBACK_KEY,
  FILTER_PROMPT_HEADER_TEXT_KEY,
  FILTER_PROMPT_QUERY_KEY,
  FILTER_PROMPT_TRIGGER_TEXT_KEY,
  FILTER_PROMPT_VALUES_KEY,
  MULTI_VALUE_FILTER_DELIMITER,
  type TableFilter,
  type TableFilterOption,
} from '@fly/shared';

import { SerializedFilter, TableStateType } from '../TanstackTable.types';

/**
 * Applies table filters to data. Used for both full client-side filtering and
 * client-only filtering when dataProcessing='server' (applyClientSide filters).
 */
function applyFiltersToData<T extends object>(
  data: T[],
  filters: TableFilter[],
  possibleFilters: TableFilterOption<T>[],
  includeFilter: (filterOption: TableFilterOption<T>) => boolean
): T[] {
  let tmpFilteredData = data;
  const actualFilters: ((value: T) => boolean)[] = [];

  for (const filter of filters) {
    const filterOption = possibleFilters.find((f) => f.key === filter.key);
    if (
      !filterOption ||
      filter.value === undefined ||
      !includeFilter(filterOption)
    ) {
      continue;
    }

    const singleMatch = filterOption.possibleValues?.find(
      (possibleValue) => possibleValue.value === filter.value
    );
    if (singleMatch?.filter) {
      actualFilters.push(singleMatch.filter);
      continue;
    }
    if (filter.value.includes(MULTI_VALUE_FILTER_DELIMITER)) {
      const parts = filter.value
        .split(MULTI_VALUE_FILTER_DELIMITER)
        .filter(Boolean);
      const filtersForParts = parts
        .map(
          (part) =>
            filterOption.possibleValues?.find((pv) => pv.value === part)?.filter
        )
        .filter((fn): fn is (value: T) => boolean => typeof fn === 'function');
      if (filtersForParts.length > 0) {
        actualFilters.push((row: T) => filtersForParts.some((fn) => fn(row)));
      }
    }
  }

  actualFilters.forEach((filterFn) => {
    tmpFilteredData = tmpFilteredData.filter(filterFn);
  });
  return tmpFilteredData;
}

export function serializeFilter(filter: TableFilter): SerializedFilter {
  return {
    key: `${FILTER_PARAM_PREFIX}${filter.key}`,
    value: filter.value || '',
  };
}

function deserializeFilter(searchParams: URLSearchParams): SerializedFilter[] {
  const filters: SerializedFilter[] = [];
  const prefix = FILTER_PARAM_PREFIX;
  searchParams.forEach((value, key) => {
    if (key.startsWith(prefix)) {
      filters.push({ key: key.slice(prefix.length), value });
    }
  });
  return filters;
}

export function useTableFilters<T extends object>(
  data: T[],
  possibleFilters: TableFilterOption<T>[],
  tableState: TableStateType,
  onChange?: (filters: TableFilter[]) => void
) {
  const { state } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filtersState, setFiltersState] = useState<TableFilter[]>([]);
  const [activeFilters, setActiveFilters] = useState<TableFilter[]>([]);

  const setFilters = useCallback(
    (filters: TableFilter[]) => {
      const areFiltersFinished = filters.every((filter) => !!filter.value);

      if (areFiltersFinished) {
        onChange?.(filters);
      }

      if (tableState === 'state') {
        setFiltersState(filters);
        return;
      }

      setSearchParams(
        (params) => {
          const next = new URLSearchParams(params);
          // Collect keys first to avoid mutating URLSearchParams while iterating
          // (some environments skip entries when deleting during forEach).
          // Preserve prompt-scoped URL params so adding a descriptor filter (e.g.
          // author) does not remove prompt UI / deployment-wave deep links.
          const promptParamKeys = new Set([
            `${FILTER_PARAM_PREFIX}${FILTER_PROMPT_QUERY_KEY}`,
            `${FILTER_PARAM_PREFIX}${FILTER_PROMPT_VALUES_KEY}`,
            `${FILTER_PARAM_PREFIX}${FILTER_PROMPT_DEPLOYMENT_KEY}`,
            `${FILTER_PARAM_PREFIX}${FILTER_PROMPT_DEPLOYMENT_ID_KEY}`,
            `${FILTER_PARAM_PREFIX}${FILTER_PROMPT_TRIGGER_TEXT_KEY}`,
            `${FILTER_PARAM_PREFIX}${FILTER_PROMPT_HEADER_TEXT_KEY}`,
            `${FILTER_PARAM_PREFIX}${FILTER_PROMPT_DEPLOY_COUNT_KEY}`,
            `${FILTER_PARAM_PREFIX}${FILTER_PROMPT_DEPLOY_ROLLBACK_KEY}`,
          ]);
          const keysToDelete: string[] = [];
          next.forEach((_, key) => {
            if (
              key.startsWith(FILTER_PARAM_PREFIX) &&
              !promptParamKeys.has(key)
            ) {
              keysToDelete.push(key);
            }
          });
          keysToDelete.forEach((key) => next.delete(key));
          filters.forEach((filter) => {
            const { key, value } = serializeFilter(filter);
            next.set(key, value);
          });
          return next;
        },
        { replace: true, state }
      );
    },
    [onChange, setSearchParams, state, tableState]
  );

  const filters: TableFilter[] = useMemo(() => {
    if (tableState === 'state') {
      return filtersState;
    }

    const deserializedFilters = deserializeFilter(searchParams);

    if (!deserializedFilters || deserializedFilters.length === 0) {
      return [];
    }

    const newFilters: TableFilter[] = [];
    for (const filter of deserializedFilters) {
      const filterOption = possibleFilters.find((f) => f.key === filter.key);

      if (!filterOption) {
        continue;
      }

      const filterValue = filterOption.possibleValues?.find(
        (possibleValue) => possibleValue.value === filter.value
      );
      const isMultiValue =
        !filterValue && filter.value?.includes(MULTI_VALUE_FILTER_DELIMITER);

      newFilters.push({
        name: filterOption.name,
        key: filterOption.key,
        value: filterValue?.value ?? (isMultiValue ? filter.value : undefined),
      });
    }

    // Keep filter order stable (match possibleFilters order) so adding a filter
    // doesn't reorder chips (e.g. Author then Last run stays Author, Last run).
    newFilters.sort((a, b) => {
      const indexA = possibleFilters.findIndex((f) => f.key === a.key);
      const indexB = possibleFilters.findIndex((f) => f.key === b.key);
      return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
    });

    return newFilters;
  }, [possibleFilters, searchParams, filtersState, tableState]);

  const addFilter = useCallback(
    (filter: TableFilterOption<T>) => {
      const newFilters = [
        ...filters,
        {
          name: filter.name,
          key: filter.key,
          value: '',
        },
      ];

      setFilters(newFilters);
    },
    [filters, setFilters]
  );

  const changeFilter = useCallback(
    (key: string, value: string) => {
      const newFilters = filters.map((f) =>
        f.key === key ? { ...f, value } : f
      );

      setFilters(newFilters);
    },
    [filters, setFilters]
  );

  const removeFilter = useCallback(
    (key: string) => {
      const newFilters = filters.filter((f) => f.key !== key);

      setFilters(newFilters);
    },
    [filters, setFilters]
  );

  // When user clicks "Clear Filter", remove every query param whose key starts with f.
  // (e.g. f.author, f.lastRun, f.prompt_query, f.prompt_values, f.prompt_deployment) so all filters are cleared.
  const clearFilters = useCallback(() => {
    if (tableState === 'state') {
      setFilters([]);
      return;
    }
    const prefix = FILTER_PARAM_PREFIX; // 'f.'
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params);
        const keysToDelete: string[] = [];
        next.forEach((_, key) => {
          if (key.startsWith(prefix)) {
            keysToDelete.push(key);
          }
        });
        keysToDelete.forEach((key) => next.delete(key));
        return next;
      },
      { replace: true, state }
    );
  }, [setFilters, tableState, setSearchParams, state]);

  const filteredData = useMemo(
    () => applyFiltersToData(data, filters, possibleFilters, () => true),
    [data, filters, possibleFilters]
  );

  /** Data filtered only by filters with applyClientSide (clientFilterFn). Used when dataProcessing='server' so client-capable filters still apply locally. */
  const clientFilteredData = useMemo(
    () =>
      applyFiltersToData(data, filters, possibleFilters, (fo) =>
        Boolean(fo.applyClientSide)
      ),
    [data, filters, possibleFilters]
  );

  /**
   * Need to track the active filters to avoid unnecessary re-renders
   * when the filters are changed.
   * This is because the filters have intermediate state with empty value.
   */
  useEffect(() => {
    const newActiveFilters = filters.filter(
      (filter) => filter.value !== undefined
    );

    const isChanged =
      JSON.stringify(newActiveFilters) !== JSON.stringify(activeFilters);

    if (isChanged) {
      setActiveFilters(newActiveFilters);
    }
  }, [filters, activeFilters, onChange]);

  return {
    filters,
    activeFilters,
    addFilter,
    removeFilter,
    changeFilter,
    clearFilters,
    filteredData,
    clientFilteredData,
  };
}
